import React, { useEffect, useState } from "react";
import { getBackupStatus, listS3Backups, triggerS3Backup, getBackupDownloadUrl, sendTestBackupEmail } from "../lib/api";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { useToast } from "../hooks/use-toast";
import { Download, RefreshCw, Mail, Database, Clock, CheckCircle, AlertCircle } from "lucide-react";

export default function BackupsTab() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  async function loadData() {
    setLoading(true);
    try {
      const [statusRes, backupsRes] = await Promise.all([
        getBackupStatus(),
        listS3Backups()
      ]);
      setStatus(statusRes);
      setBackups(backupsRes.backups || []);
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Failed to load backup data" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleTriggerBackup() {
    setTriggeringBackup(true);
    try {
      const result = await triggerS3Backup();
      toast({ 
        title: "Backup created successfully",
        description: `Backup size: ${result.size_mb?.toFixed(2)} MB`
      });
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Backup failed" });
    } finally {
      setTriggeringBackup(false);
    }
  }

  async function handleSendTestEmail() {
    setSendingTestEmail(true);
    try {
      const result = await sendTestBackupEmail();
      toast({ 
        title: "Test email sent",
        description: result.message
      });
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Failed to send test email" });
    } finally {
      setSendingTestEmail(false);
    }
  }

  async function handleDownload(s3Key) {
    setDownloadingKey(s3Key);
    try {
      const result = await getBackupDownloadUrl(s3Key);
      window.open(result.download_url, '_blank');
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Failed to get download URL" });
    } finally {
      setDownloadingKey(null);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleString();
  }

  function formatSize(mb) {
    if (!mb) return "—";
    if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
    return `${mb.toFixed(2)} MB`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backups-tab">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* S3 Configuration */}
        <div className="rounded border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">S3 Storage</span>
          </div>
          <div className="flex items-center gap-2">
            {status?.configured ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600">Connected</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-600">Not configured</span>
              </>
            )}
          </div>
          {status?.bucket && (
            <p className="text-xs text-muted-foreground mt-1 truncate" title={status.bucket}>
              Bucket: {status.bucket}
            </p>
          )}
        </div>

        {/* Email Configuration */}
        <div className="rounded border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Email Notifications</span>
          </div>
          <div className="flex items-center gap-2">
            {status?.email_configured ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600">Configured</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-600">Not configured</span>
              </>
            )}
          </div>
          {status?.notification_email && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              To: {status.notification_email}
            </p>
          )}
        </div>

        {/* Scheduler Status */}
        <div className="rounded border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Auto Backup Schedule</span>
          </div>
          <div className="flex items-center gap-2">
            {status?.scheduler?.running ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600">Running</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-600">Not running</span>
              </>
            )}
          </div>
          {status?.scheduler?.jobs?.[0]?.next_run && (
            <p className="text-xs text-muted-foreground mt-1">
              Next: {formatDate(status.scheduler.jobs[0].next_run)}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button 
          onClick={handleTriggerBackup} 
          disabled={triggeringBackup || !status?.configured}
          data-testid="trigger-backup-btn"
        >
          {triggeringBackup ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Creating Backup...
            </>
          ) : (
            <>
              <Database className="h-4 w-4 mr-2" />
              Create Backup Now
            </>
          )}
        </Button>
        
        <Button 
          variant="secondary"
          onClick={handleSendTestEmail} 
          disabled={sendingTestEmail || !status?.email_configured}
          data-testid="send-test-email-btn"
        >
          {sendingTestEmail ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Send Test Email
            </>
          )}
        </Button>

        <Button 
          variant="outline"
          onClick={loadData}
          className="ml-auto"
          data-testid="refresh-backups-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Backup History */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Backup History</h3>
        <p className="text-xs text-muted-foreground">
          Backups are stored as ZIP files containing CSV exports of all collections. Downloads expire after 1 hour.
        </p>
        
        <div className="rounded border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No backups found. Click "Create Backup Now" to create your first backup.
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup, idx) => (
                  <TableRow key={backup.key || idx}>
                    <TableCell className="text-sm">
                      {formatDate(backup.last_modified)}
                    </TableCell>
                    <TableCell className="text-sm font-mono text-muted-foreground truncate max-w-[200px]" title={backup.key}>
                      {backup.key?.split('/').pop() || backup.key}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {formatSize(backup.size_mb)}
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDownload(backup.key)}
                        disabled={downloadingKey === backup.key}
                        data-testid={`download-backup-${idx}`}
                      >
                        {downloadingKey === backup.key ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded border p-4 bg-muted/50">
        <h4 className="text-sm font-medium mb-2">About Backups</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• <strong>Format:</strong> ZIP file containing CSV exports of all database collections</li>
          <li>• <strong>Schedule:</strong> Automatic backups run every Sunday at 2:00 AM EST</li>
          <li>• <strong>Retention:</strong> Backups are retained for 30 days, then automatically deleted</li>
          <li>• <strong>Storage:</strong> AWS S3 with server-side encryption (AES-256)</li>
          <li>• <strong>Email:</strong> Notification emails include a direct download link (valid for 1 hour)</li>
        </ul>
      </div>
    </div>
  );
}
